import type { Exchange, Operation } from '@urql/core'
import { filter, makeSubject, merge, onPush, pipe, share } from 'wonka'
import { offlineSyncStore } from './offlineSync'

/**
 * One mutation in flight at a time (FIFO) — graphcache replays its offline
 * queue concurrently, which raced dependent chains like clock-out→clock-in
 * (CRE-80). Queries/teardowns pass through untouched.
 */

// Releases a wedged slot (fetch has no timeout); late results are ignored.
export const SAFETY_TIMEOUT_MS = 30_000

const isMutation = (op: Operation) => op.kind === 'mutation'
const isTeardown = (op: Operation) => op.kind === 'teardown'

export const serializeMutationsExchange: Exchange = ({ forward }) => {
  const held: Operation[] = []
  let activeKey: number | null = null
  let releaseTimer: ReturnType<typeof setTimeout> | undefined
  const { source: released$, next: release } = makeSubject<Operation>()

  const publish = () =>
    offlineSyncStore.setInFlightMutations(
      held.length + (activeKey !== null ? 1 : 0),
    )

  const releaseNext = () => {
    clearTimeout(releaseTimer)
    activeKey = null
    const next = held.shift()
    if (next) dispatch(next)
    else publish()
  }

  const dispatch = (op: Operation) => {
    activeKey = op.key
    clearTimeout(releaseTimer)
    releaseTimer = setTimeout(releaseNext, SAFETY_TIMEOUT_MS)
    publish()
    release(op)
  }

  return (ops$) => {
    const shared$ = pipe(ops$, share)

    const passThrough$ = pipe(
      shared$,
      filter((op: Operation) => !isMutation(op)),
      onPush((op: Operation) => {
        if (!isTeardown(op)) return
        const heldIndex = held.findIndex((h) => h.key === op.key)
        if (heldIndex > -1) {
          held.splice(heldIndex, 1)
          publish()
          return
        }
        if (op.key === activeKey) releaseNext()
      }),
    )

    const gate$ = pipe(
      shared$,
      filter(isMutation),
      onPush((op: Operation) => {
        if (activeKey === null) {
          dispatch(op)
          return
        }
        held.push(op)
        publish()
      }),
      // side-effect-only branch: mutations reach forward via released$
      filter(() => false),
    )

    return pipe(
      forward(merge([passThrough$, gate$, released$])),
      onPush((result) => {
        const isActiveMutationResult =
          isMutation(result.operation) && result.operation.key === activeKey
        if (isActiveMutationResult) releaseNext()
      }),
    )
  }
}
